import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Spinner,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  Badge,
  Modal,
  InputGroup,
  Pagination,
  Dropdown,
  FormCheck,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const UserManagement = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
    employee_id: "",
  });
  const [earnings, setEarnings] = useState([]);
  const [deductions, setDeductions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } = getActionPermissions(userRole);
  const userId = sessionStorage.getItem("userId");
  const companyKey = sessionStorage.getItem("companyKey");

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );

        const filteredUsers = response.data.filter((d) => d.companyKey === companyKey);
        // Filter only approved users
        const approvedUsers = filteredUsers.filter(
          (user) =>
            user.approval_status === true ||
            user.approval_status === "true" ||
            user.approval_status === "1"
        );
        setUsers(approvedUsers.reverse());
        setFilteredUsers(approvedUsers);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching users:", error);
        showMyAlert({
          type: "danger",
          title: "Error",
          message: "Failed to load users. Please try again later.",
          duration: 2500,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filterUsers = () => {
    let filtered = [...users];

    // Apply search filter if there's a query
    if (searchQuery.trim() !== "") {
      filtered = filtered.filter((user) => {
        const searchableFields = [
          user.full_name || "",
          user.email || "",
          user.mobile_number || "",
          user.employee_id || "",
          user.id?.toString() || "",
        ];

        return searchableFields.some((field) =>
          field.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  //   const handleDeleteClick = (user) => {
  //     setSelectedUser(user);
  //     setShowDeleteModal(true);
  //   };

  const handleDeleteConfirm = async () => {
    try {
      const oldData = { ...selectedUser };
      const newData = { ...selectedUser, status: "deleted" };

      await axios.delete(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`
      );

doAuditLog("Delete", "Payslip Configurator", selectedUser, "");
      const updatedUsers = users.filter((user) => user.id !== selectedUser.id);
      setUsers(updatedUsers);
      setShowDeleteModal(false);
                                                                  showMyAlert({
  type: "success",              // "success" | "danger" | "warning" | "info"
  title: "Deleted",
  message: `User "${selectedUser.full_name}" deleted successfully`,
  duration: 2500,               // auto-close in 2.5s
});

    } catch (error) {
      console.error("Error deleting user:", error);
      showMyAlert({
        type: "danger",
        title: "Error",
        message: "Failed to delete user. Please try again.",
        duration: 2500,
      });

    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      full_name: user.full_name,
      email: user.email,
      mobile_number: user.mobile_number,
      employee_id: user.employee_id,
    });

    // Parse earnings and deductions
    try {
      const parsedEarnings = user.earnings ? JSON.parse(user.earnings) : [];
      const parsedDeductions = user.deductions
        ? JSON.parse(user.deductions)
        : [];

      setEarnings(parsedEarnings);
      setDeductions(parsedDeductions);
    } catch (e) {
      console.error("Error parsing earnings/deductions:", e);
      setEarnings([]);
      setDeductions([]);
    }

    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (type, key, value) => {
    if (type === "earnings") {
      const updated = earnings.map((item) => {
        if (Object.keys(item)[0] === key) {
          return { [key]: value ? "true" : "false" };
        }
        return item;
      });
      setEarnings(updated);
    } else {
      const updated = deductions.map((item) => {
        if (Object.keys(item)[0] === key) {
          return { [key]: value ? "true" : "false" };
        }
        return item;
      });
      setDeductions(updated);
    }
  };

  const handleEditSubmit = async () => {
    try {
      const oldData = { ...selectedUser };

      // Prepare earnings and deductions data
      const earningsData =
        earnings.length > 0 ? JSON.stringify(earnings) : "[]";
      const deductionsData =
        deductions.length > 0 ? JSON.stringify(deductions) : "[]";

      const dataToSend = {
        ...editFormData,
        earnings: earningsData,
        deductions: deductionsData,
      };

      const newData = { ...selectedUser, ...dataToSend };

      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`,
        dataToSend
      );

      doAuditLog("Update", "Payslip Configurator", selectedUser, newData);

      const updatedUsers = users.map((user) =>
        user.id === selectedUser.id ? newData : user
      );
      setUsers(updatedUsers);
      setShowEditModal(false);
                                                                  showMyAlert({
  type: "success",              // "success" | "danger" | "warning" | "info"
  title: "Updated",
  message: `User "${selectedUser.full_name}" updated successfully`,
  duration: 2500,               // auto-close in 2.5s
});
    } catch (error) {
      console.error("Error updating user:", error);
      showMyAlert({
        type: "danger",
        title: "Error",
        message: "Failed to update user. Please try again.",
        duration: 2500,
      });
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => paginate(1)}
        disabled={currentPage === 1}
      />
    );

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      items.push(<Pagination.Ellipsis key="end-ellipsis" />);
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    items.push(
      <Pagination.Last
        key="last"
        onClick={() => paginate(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div
        className={
          isMobile
            ? "flex-column align-items-start"
            : "d-flex justify-content-between align-items-center mt-3 mb-3"
        }
        style={isMobile ? { gap: "1rem", marginTop: "1rem" } : {}}
      >
        <div
          className="d-flex align-items-center"
          style={isMobile ? { width: "100%" } : {}}
        >
          <span className="me-2">Items per page:</span>
          <Dropdown style={isMobile ? { width: "100%" } : {}}>
            <Dropdown.Toggle
              variant="outline-primary"
              id="dropdown-items-per-page"
              style={isMobile ? { width: "100%" } : {}}
            >
              {itemsPerPage}
            </Dropdown.Toggle>
            <Dropdown.Menu style={isMobile ? { width: "100%" } : {}}>
              {[5, 10, 20, 50, 100].map((count) => (
                <Dropdown.Item
                  key={count}
                  onClick={() => handleItemsPerPageChange(count)}
                  active={itemsPerPage === count}
                >
                  {count}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <span
            className="ms-3"
            style={isMobile ? { marginLeft: "0.5rem" } : {}}
          >
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredUsers.length)} of{" "}
            {filteredUsers.length} entries
          </span>
        </div>
        <div style={isMobile ? { width: "100%" } : {}}>
          <Pagination className={isMobile ? "mt-2" : "mb-0"}>
            {items}
          </Pagination>
        </div>
      </div>
    );
  };

  const isMobile = window.innerWidth < 768;

  return (
    <Container className="mt-3 px-2">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12">
          <h5 className="mb-0 d-flex align-items-center">
            Payroll Configuration
          </h5>
        </div>

      </div>

      {/* Search Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-4">
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <Search size={16} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search here..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <div className="row align-items-center justify-content-between mb-2 py-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto mb-2 mb-md-0">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="dropdown-items-per-page"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>



          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination size="sm" className="mb-0 justify-content-center justify-content-md-end">
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item onClick={() => paginate(totalPages)}>
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show smart pagination */
                (() => {
                  const items = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  if (startPage > 1) {
                    items.push(<Pagination.Ellipsis key="start-ellipsis" />);
                  }

                  for (let number = startPage; number <= endPage; number++) {
                    items.push(
                      <Pagination.Item
                        key={number}
                        active={number === currentPage}
                        onClick={() => paginate(number)}
                      >
                        {number}
                      </Pagination.Item>
                    );
                  }

                  if (endPage < totalPages) {
                    items.push(<Pagination.Ellipsis key="end-ellipsis" />);
                  }

                  return items;
                })()
              )}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}



      <div className="table-responsive">
        {isMobile ? (
          currentItems.length > 0 ? (
            currentItems.map((user, index) => {
              let userEarnings = [];
              let userDeductions = [];
              try {
                userEarnings = user.earnings ? JSON.parse(user.earnings) : [];
                userDeductions = user.deductions
                  ? JSON.parse(user.deductions)
                  : [];
              } catch (e) {
                console.error("Error parsing earnings/deductions:", e);
              }
              const activeEarnings = userEarnings
                .filter((item) => Object.values(item)[0] === "true")
                .map((item) => Object.keys(item)[0]);
              const activeDeductions = userDeductions
                .filter((item) => Object.values(item)[0] === "true")
                .map((item) => Object.keys(item)[0]);
              return (
                <div key={user.id} className="mb-3">
                  <div
                    className="card shadow-sm"
                    style={{ textAlign: "left", borderRadius: "12px" }}
                  >
                    <div className="card-body table-responsive">
                      <table className="table table-striped table-bordered mb-2">
                        <tbody>
                          <tr>
                            <td style={{ minWidth: 150 }} className="fw-bold">SI</td>
                            <td>{filteredUsers.length - (indexOfFirstItem + index)}</td>
                          </tr>
                          <tr>
                            <td style={{ minWidth: 150 }} className="fw-bold">Full Name</td>
                            <td>{user.full_name}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Email ID</td>
                            <td>{user.email || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Mobile Number</td>
                            <td>{user.mobile_number}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Employee ID</td>
                            <td>{user.employee_id || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Earnings</td>
                            <td>
                              {activeEarnings.length > 0
                                ? activeEarnings.join(", ")
                                : "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Deductions</td>
                            <td>
                              {activeDeductions.length > 0
                                ? activeDeductions.join(", ")
                                : "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    {canEdit &&  <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditClick(user)}
                          disabled={userRole !== "superadmin"}
                        >
                          Edit
                        </Button>
                      </div>}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-4">
              {searchQuery
                ? "No users found matching your search criteria."
                : "No approved users found."}
            </div>
          )
        ) : (
          <Table responsive striped bordered hover className="mb-0">
            <thead className="">
              <tr>
                <th id="text-white-important">S.I</th>
                <th id="text-white-important">Full Name</th>
                <th id="text-white-important">Email ID</th>
                <th id="text-white-important">Mobile Number</th>
                <th id="text-white-important">Employee ID</th>
                <th id="text-white-important">Earnings</th>
                <th id="text-white-important">Deductions</th>
              {canEdit &&  <th id="text-white-important">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0">Loading users...</p>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((user, index) => {
                  // Parse earnings and deductions
                  let userEarnings = [];
                  let userDeductions = [];

                  try {
                    userEarnings = user.earnings
                      ? JSON.parse(user.earnings)
                      : [];
                    userDeductions = user.deductions
                      ? JSON.parse(user.deductions)
                      : [];
                  } catch (e) {
                    console.error("Error parsing earnings/deductions:", e);
                  }

                  // Get active earnings and deductions
                  const activeEarnings = userEarnings
                    .filter((item) => Object.values(item)[0] === "true")
                    .map((item) => Object.keys(item)[0]);

                  const activeDeductions = userDeductions
                    .filter((item) => Object.values(item)[0] === "true")
                    .map((item) => Object.keys(item)[0]);

                  return (
                    <tr key={user.id}>
                      <td>{filteredUsers.length - (indexOfFirstItem + index)}</td>
                      <td>{user.full_name}</td>
                      <td>{user.email || "N/A"}</td>
                      <td>{user.mobile_number}</td>
                      <td>{user.employee_id || "N/A"}</td>
                      <td>
                        {activeEarnings.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {activeEarnings.map((item, i) => (
                              <Badge
                                key={`earn-${i}`}
                                bg="success"
                                className="d-flex align-items-center"
                              >
                                <span className="me-1">✓</span> {item}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {activeDeductions.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {activeDeductions.map((item, i) => (
                              <Badge
                                key={`deduct-${i}`}
                                bg="danger"
                                className="d-flex align-items-center"
                              >
                                <span className="me-1">✓</span> {item}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
{ canEdit &&                     <td>
                        <ButtonGroup size="sm gap-2">
{                          <Button
                            variant="outline-primary"
                            onClick={() => handleEditClick(user)}
                          >
                            Edit
                          </Button>}
                        </ButtonGroup>
                      </td>}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    {searchQuery
                      ? "No users found matching your search criteria."
                      : "No approved users found."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>


      {/* Showing entries info */}
      <div className="col-12 mb-2 text-end my-2">
        <span className="text-muted">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} entries
        </span>
      </div>
      {/* Edit User Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit User: {selectedUser?.full_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={editFormData.full_name}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile_number"
                    value={editFormData.mobile_number}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Employee ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="employee_id"
                    value={editFormData.employee_id}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-4">Earnings</h5>
            <Row>
              {earnings.map((item, index) => {
                const key = Object.keys(item)[0];
                const value = item[key] === "true";
                return (
                  <Col md={4} key={`earn-${index}`}>
                    <Form.Group className="mb-3">
                      <FormCheck
                        type="checkbox"
                        label={key}
                        checked={value}
                        onChange={(e) =>
                          handleCheckboxChange(
                            "earnings",
                            key,
                            e.target.checked
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>

            <h5 className="mt-4">Deductions</h5>
            <Row>
              {deductions.map((item, index) => {
                const key = Object.keys(item)[0];
                const value = item[key] === "true";
                return (
                  <Col md={4} key={`deduct-${index}`}>
                    <Form.Group className="mb-3">
                      <FormCheck
                        type="checkbox"
                        label={key}
                        checked={value}
                        onChange={(e) =>
                          handleCheckboxChange(
                            "deductions",
                            key,
                            e.target.checked
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;
